import { Flex } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { SkuComponentType } from "__generated__/gql/graphql";
import LabeledValue from "components/Shared/LabeledValue";
import { makeFormatCurrency } from "utils/formatting";
import { selectedProductLine } from "utils/links";
import Link from "components/Shared/Link";
import CopyableCode from "components/Shared/CopyableCode";
import {
  formatUnitNetWeight,
  formatWeight,
  formatCaseCube,
  formatTiHi,
} from "components/Products/utils";
import NamingOptions from "components/Products/NamingOptions";
import SkuComponentsTable from "./SkuComponentsTable";

const formatCurrency = makeFormatCurrency({ minimumFractionDigits: 2 });

const skuDetailsTabFragment = gql(/* GraphQL */ `
  fragment SkuDetailsTab on Sku {
    id
    name
    shortName
    internalSkuId
    unitNetWeight
    unitNetWeightUnit
    grossPalletWeightLbs
    unitCogs
    unitSrp
    productLine {
      id
      name
      shortName
      nameSeparator
      isInternalSkuIdAppended
    }
    tiHi {
      id
      ti
      hi
    }
    ...SkuComponentsTable
    skuComponentsList {
      id
      skuComponentType
      dimensions {
        id
        cube
      }
    }
  }
`);

type Props = {
  sku: FragmentType<typeof skuDetailsTabFragment> | null | undefined;
};

export default function SkuDetailsTab({ sku: skuFragment }: Props) {
  const sku = getFragmentData(skuDetailsTabFragment, skuFragment);
  const skuComponents = sku?.skuComponentsList || [];
  const tiHi = sku?.tiHi;
  const productLine = sku?.productLine;

  const caseSkuComponent = skuComponents.find(
    (sc) => sc.skuComponentType === SkuComponentType.CASE,
  );

  return (
    <>
      <NamingOptions
        heading="Name Formats"
        productLineName={productLine?.name}
        productLineShortName={productLine?.shortName}
        skuName={sku?.name}
        skuShortName={sku?.shortName}
        nameSeparator={productLine?.nameSeparator}
        isInternalSkuIdAppended={productLine?.isInternalSkuIdAppended}
        internalSkuId={sku?.internalSkuId}
      />

      <Flex>
        <LabeledValue
          label="Product Line"
          value={
            productLine?.name && (
              <Link href={selectedProductLine(productLine.id)} shallow>
                {productLine.name}
              </Link>
            )
          }
        />

        <LabeledValue
          label="Internal SKU ID"
          value={
            sku?.internalSkuId && <CopyableCode code={sku.internalSkuId} />
          }
        />
      </Flex>

      <Flex>
        <LabeledValue
          label="Unit Net Weight"
          value={formatUnitNetWeight(
            sku?.unitNetWeight,
            sku?.unitNetWeightUnit,
          )}
        />
      </Flex>

      <Flex>
        <LabeledValue label="Unit COGS" value={formatCurrency(sku?.unitCogs)} />
        <LabeledValue label="Unit SRP" value={formatCurrency(sku?.unitSrp)} />
      </Flex>

      <SkuComponentsTable sku={sku} />

      <Flex gap={2} sx={{ dt: { isTruncated: true } }}>
        <LabeledValue
          label="Case Cube"
          value={formatCaseCube(caseSkuComponent?.dimensions?.cube)}
        />

        <LabeledValue label="Ti/Hi" value={formatTiHi(tiHi?.ti, tiHi?.hi)} />

        <LabeledValue
          label="Gross Pallet Weight"
          value={formatWeight(sku?.grossPalletWeightLbs)}
        />
      </Flex>
    </>
  );
}

export { skuDetailsTabFragment };
